import queryString from 'query-string'
import { isEmpty, sortBy } from 'lodash'

import { apiErrorMessage, apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

// agent-gpt admits the orgs the caller may act for (their own, a hierarchy
// manager's child orgs, any org for Balto staff) with the AgentGPT Editor grant.
// The header carries the caller's home org; the target rides in body or query.
const SP_BASE = () => `${apiService.agentGpt}/sharepoint_connections`
const USE_OWN_ORG = true

// Every SharePoint connection the org holds (QCER-1026: one per Entra tenant),
// oldest first. Creation order is stable, so rows keep their position across a
// save instead of reshuffling under the operator's cursor. Load errors
// propagate so the panel can tell "no connections yet" (show the empty state
// and its add path) apart from "load failed" (show the error) — otherwise a
// failed load looks like an empty org and invites a duplicate create.
export const fetchSharePointConnections =
  ({ organizationId }) =>
  async (dispatch) => {
    const rows = await fetchingAPI(
      `${SP_BASE()}?organization_id=${organizationId}`,
      'GET',
      dispatch,
      undefined,
      undefined,
      {},
      USE_OWN_ORG
    )
    return sortBy(rows, 'id')
  }

// Per-selector ingest state persisted by the SharePoint flow (QCER-1023):
// what each configured site/library actually resolved to, how many documents
// it holds, when it last synced, and the last error. Load failures resolve to
// null, which the panel renders as its own "couldn't load" line (distinct
// from the never-synced empty state) — either way the config form stays
// usable when the state endpoint is unavailable.
export const fetchSharePointSyncState =
  ({ connectionId }) =>
  async (dispatch) => {
    try {
      return await fetchingAPI(
        `${SP_BASE()}/${connectionId}/sync_state`,
        'GET',
        dispatch,
        undefined,
        undefined,
        {},
        USE_OWN_ORG
      )
    } catch (error) {
      return null
    }
  }

export const createSharePointConnection =
  ({ organizationId, label, tenantId, tenantHostname, allowlist, active }) =>
  async (dispatch) => {
    try {
      const created = await fetchingAPI(
        SP_BASE(),
        'POST',
        dispatch,
        JSON.stringify({
          organization_id: organizationId,
          label,
          tenant_id: tenantId,
          tenant_hostname: tenantHostname,
          allowlist,
          active,
        }),
        undefined,
        {},
        USE_OWN_ORG
      )
      toast.success('SharePoint connection created')
      return created
    } catch (error) {
      toast.error('Failed to create SharePoint connection', { cause: error })
      return null
    }
  }

export const updateSharePointConnection =
  ({ connectionId, patch, successMessage }) =>
  async (dispatch) => {
    try {
      const updated = await fetchingAPI(
        `${SP_BASE()}/${connectionId}`,
        'PATCH',
        dispatch,
        JSON.stringify(patch),
        undefined,
        {},
        USE_OWN_ORG
      )
      toast.success(successMessage || 'SharePoint connection updated')
      return updated
    } catch (error) {
      toast.error('Failed to update SharePoint connection', { cause: error })
      return null
    }
  }

// QCER-1024: remove ONE allowlist selector and purge its ingested documents in
// a single agent-gpt operation, so config and data cannot drift apart. Omit
// libraryName to remove a whole-site selector (purges all of the site's
// libraries plus its Site Pages). The deleted-count toast is the operator's
// receipt that the purge actually ran; a failure returns null so the caller
// keeps its confirmation open for a retry.
export const removeSharePointAllowlistEntry =
  ({ connectionId, sitePath, libraryName, listName }) =>
  async (dispatch) => {
    try {
      // stringify drops undefined values, so a site-wide removal (no
      // libraryName, no listName) sends only site_path. The caller passes at
      // most one of the two name params — the endpoint 400s on both (QCER-1089).
      const query = queryString.stringify({
        site_path: sitePath,
        library_name: libraryName,
        list_name: listName,
      })
      // QCER-1089: a list removal uses its own path on purpose. On the shared
      // path a pre-QCER-1089 server would IGNORE the unknown list_name param,
      // read the absent library_name as the whole-site spelling, and purge the
      // entire site's corpus with a 200 — so rolling agent-gpt back while this
      // panel is live would erase a customer's site behind a success toast.
      // This path simply does not exist on that server, so the call 404s and
      // the allowlist is left unedited.
      const path = isEmpty(listName)
        ? `${SP_BASE()}/${connectionId}/allowlist_entries`
        : `${SP_BASE()}/${connectionId}/allowlist_entries/lists`
      const removed = await fetchingAPI(
        `${path}?${query}`,
        'DELETE',
        dispatch,
        undefined,
        undefined,
        {},
        USE_OWN_ORG
      )
      let target
      if (!isEmpty(removed.list_name)) {
        target = `list ${removed.list_name} in ${removed.site_path}`
      } else if (removed.site_wide) {
        target = `${removed.site_path} (whole site)`
      } else {
        target = `${removed.library_name} in ${removed.site_path}`
      }
      toast.success(`Removed ${target} — ${removed.objects_deleted} indexed objects purged`)
      return removed
    } catch (error) {
      toast.error('Failed to remove the allowlist entry', { cause: error })
      return null
    }
  }

// Erase everything one connection has ingested (agent-gpt's offboarding /
// GDPR purge). The connection ROW survives, stamped `purged_at` as the erasure
// marker — there is no delete-the-connection endpoint, so this is the panel's
// destructive action for a connection.
//
// Two server rules the panel has to respect rather than duplicate: the purge is
// refused with 409 while the connection is still active (deactivate first), and
// it is scoped so a sibling tenant sharing the org's Weaviate collection is
// never touched. The deleted-count toast is the operator's receipt that the
// erasure actually ran; a failure returns null so the caller keeps its
// confirmation open for a retry.
export const purgeSharePointDocuments =
  ({ connectionId }) =>
  async (dispatch) => {
    try {
      const purged = await fetchingAPI(
        `${SP_BASE()}/${connectionId}/documents`,
        'DELETE',
        dispatch,
        undefined,
        undefined,
        {},
        USE_OWN_ORG
      )
      toast.success(`Documents erased — ${purged.objects_deleted} indexed objects purged`)
      return purged
    } catch (error) {
      toast.error('Failed to erase the connection’s documents', { cause: error })
      return null
    }
  }

// Lists on or off for every entry of one connection. Turning off also purges
// the lists discovered on its sites; named lists stay.
export const setSharePointLists =
  ({ connectionId, enabled }) =>
  async (dispatch) => {
    try {
      const updated = await fetchingAPI(
        `${SP_BASE()}/${connectionId}/lists`,
        'PUT',
        dispatch,
        JSON.stringify({ enabled }),
        undefined,
        { isSilent: true },
        USE_OWN_ORG
      )
      toast.success(
        enabled
          ? 'Lists turned on — they sync on the next run'
          : `Lists turned off — ${updated.objects_deleted} indexed objects purged`
      )
      return updated
    } catch (error) {
      // A half-done off already purged what it could, and a retry finishes it.
      toast.error(
        apiErrorMessage(error, 'Failed to change lists for this connection. Try again to finish.')
      )
      return null
    }
  }

// Balto staff only (RTG3-1300): lets a library held for size sync anyway, or holds it again.
export const setSharePointSizeApproval =
  ({ connectionId, driveId, approved }) =>
  async (dispatch) => {
    try {
      const updated = await fetchingAPI(
        `${SP_BASE()}/${connectionId}/backfill/${encodeURIComponent(driveId)}/size_approval`,
        'PUT',
        dispatch,
        JSON.stringify({ approved }),
        undefined,
        { isSilent: true },
        USE_OWN_ORG
      )
      toast.success(
        approved
          ? 'Approved — the library syncs on the next run'
          : 'Approval withdrawn — the library is held again'
      )
      return updated
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to change the size approval'))
      return null
    }
  }
